import { Link } from "react-router-dom";
import {
  BookOpen,
  Camera,
  HeartHandshake,
  Lightbulb,
  MessageCircle,
  Newspaper,
  ShieldCheck,
  Users,
} from "lucide-react";

const memberPathways = [
  {
    title: "Find Inspiration",
    description:
      "Browse gallery collections and quest event lists, discover subjects to photograph, submit entries, and learn from thoughtful feedback.",
    icon: Camera,
    links: [
      { label: "Browse Galleries", to: "/gallery" },
      { label: "View Quests", to: "/quests" },
      { label: "What's Happening", to: "/whats-happening" },
    ],
  },
  {
    title: "Refine Your Craft",
    description:
      "Work through education resources, join workshops or events, and use mentor guidance to build practical creative skills.",
    icon: BookOpen,
    links: [
      { label: "Refine Education", to: "/refine" },
      { label: "Projects", to: "/projects" },
      { label: "Community", to: "/community" },
    ],
  },
  {
    title: "Join The Community",
    description:
      "Read and respond to blogs, contribute ideas, follow newsletters, and spend time with like minded artists.",
    icon: Users,
    links: [
      { label: "Read Blogs", to: "/blogs" },
      { label: "Community", to: "/community" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Help Others Grow",
    description:
      "Share knowledge, mentor other members, offer feedback, and support artists who are learning their next skill.",
    icon: HeartHandshake,
    links: [
      { label: "Member Projects", to: "/projects" },
      { label: "Community", to: "/community" },
      { label: "Contact LWBL", to: "/contact" },
    ],
  },
];

const supportLevels = [
  {
    title: "Basic Support",
    text: "Helps members with login, account questions, site navigation, and community feedback intake.",
    icon: MessageCircle,
  },
  {
    title: "Event Management",
    text: "Reviews quest and event entries, enters event details, and creates galleries for published work.",
    icon: Lightbulb,
  },
  {
    title: "Senior Admin",
    text: "Awards grants and badges, writes newsletters, and reviews admin work for consistency and care.",
    icon: ShieldCheck,
  },
];

function MemberPathways() {
  return (
    <section className="bg-[#f7f8f8] px-4 py-16 text-left">
      <div className="container m-auto">
        <div className="mb-10 grid gap-5 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
          <div>
            <p className="font-outfit text-xs font-bold uppercase tracking-[0.18em] text-[#008aa0]">
              Member pathways
            </p>
            <h2 className="mt-3 font-cinzel text-[38px] leading-tight text-[#101819] md:text-[58px]">
              Four ways artists move through LWBL
            </h2>
          </div>
          <p className="font-outfit text-base leading-7 text-[#536061]">
            LWBL now speaks directly to the main mindsets members bring with them: inspiration, learning,
            community, and service. Each path connects to the parts of the site that support that intention.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {memberPathways.map(({ title, description, icon: Icon, links }) => (
            <article
              key={title}
              className="flex min-h-[320px] flex-col justify-between rounded-lg border border-[#dce5e5] bg-white p-5 shadow-sm"
            >
              <div>
                <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#e7fbfd] text-[#007d91]">
                  <Icon size={22} />
                </div>
                <h3 className="font-cinzel text-2xl text-[#111718]">{title}</h3>
                <p className="mt-3 font-outfit text-sm leading-6 text-[#536061]">{description}</p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {links.map((link) => (
                  <Link
                    key={link.label}
                    to={link.to}
                    className="rounded-full border border-[#b9cfd2] px-3 py-2 font-outfit text-xs font-semibold text-[#223033] transition hover:border-[#00C3FF] hover:text-[#007d91]"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 rounded-lg border border-[#cfe0e2] bg-[#101819] p-5 text-white">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#00C3FF] text-black">
              <Newspaper size={20} />
            </div>
            <div>
              <h3 className="font-cinzel text-2xl">Volunteer support model</h3>
              <p className="font-outfit text-sm text-white/70">
                The admin team is organized by responsibility so members receive help from the right person.
              </p>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {supportLevels.map(({ title, text, icon: Icon }) => (
              <div key={title} className="rounded-lg border border-white/[0.12] bg-white/[0.06] p-4">
                <Icon className="text-[#00C3FF]" size={20} />
                <h4 className="mt-3 font-outfit text-base font-bold">{title}</h4>
                <p className="mt-2 font-outfit text-sm leading-6 text-white/70">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default MemberPathways;
